import { ACCESS_USERID_KEY } from '@/constant/config';

/**
 * 创建本地缓存对象
 * @param {string=} prefixKey -
 * @param {Object} [storage=localStorage] - sessionStorage | localStorage
 */
export const createStorage = ({ storage = localStorage } = {}) => {
  /**
   * 本地缓存类
   * @class Storage
   */
  const Storage = class {
    private storage = storage;
    private getPrefixKey(splitLimit = 0): string {
      const prefixKey = getAPKCHSource();
      if (prefixKey === 'H5US') {
        return ''; // 直接返回空前缀
      } else if (splitLimit > 0) {
        // 分割前缀并按限制数返回
        const source = prefixKey.split('_').slice(0, splitLimit);
        return source.join('_') + '_';
      }
      return prefixKey + '_';
    }

    private getKey(key: string) {
      return `${this.getPrefixKey()}${key}`.toUpperCase();
    }

    private getAccessKey(key: string) {
      return `${this.getPrefixKey(2)}${key}`.toUpperCase();
    }

    /**
     * @description 设置缓存
     * @param {string} key 缓存键
     * @param {*} value 缓存值
     * @param expire
     */
    set(key: string, value: any, expire: number | null = null) {
      const stringData = JSON.stringify({
        value,
        expire: expire !== null ? new Date().getTime() + expire * 1000 : null
      });
      this.storage.setItem(this.getKey(key), stringData);
    }
    /**
     * @description 设置缓存(跟着玩家走)
     * @param {string} key 缓存键
     * @param {*} value 缓存值
     * @param expire
     */
    setByUser(key: string, value: any, expire: number | null = null) {
      key += '_' + this.getAccess(ACCESS_USERID_KEY);
      this.set(key, value, expire);
    }
    /**
     * @description 设置缓存
     * @param {string} key 缓存键
     * @param {*} value 缓存值
     * @param expire
     */
    setAccess(key: string, value: any, expire: number | null = null) {
      const stringData = JSON.stringify({
        value,
        expire: expire !== null ? new Date().getTime() + expire * 1000 : null
      });
      this.storage.setItem(this.getAccessKey(key), stringData);
    }
    /**
     * 读取缓存(跟着玩家走)
     * @param {string} key 缓存键
     * @param {*=} def 默认值
     */
    getByUser<T = any>(key: string, def: any = null): T {
      key += '_' + this.getAccess(ACCESS_USERID_KEY);
      return this.get(key, def);
    }
    /**
     * 读取缓存
     * @param {string} key 缓存键
     * @param {*=} def 默认值
     */
    get<T = any>(key: string, def: any = null): T {
      const item = this.storage.getItem(this.getKey(key));
      if (item) {
        try {
          const data = JSON.parse(item);
          const { value, expire } = data;
          // 在有效期内直接返回
          if (expire === null || expire >= Date.now()) {
            return value;
          }
          this.remove(this.getKey(key));
        } catch (e) {
          return def;
        }
      }
      return def;
    }
    private parseItem(item: string | null, key: string, def: any = null) {
      if (!item) {
        return def;
      }
      try {
        const data = JSON.parse(item);
        const { value, expire } = data;
        // 在有效期内直接返回
        if (expire === null || expire >= Date.now()) {
          return value;
        }
        this.remove(key);
      } catch (e) {
        return def;
      }
    }
    /**
     * 获取存储项的值，如果未找到，则尝试从备用键获取并迁移
     * @template T 期望返回的值的类型，默认为 any
     * @param {string} key 存储项的键
     * @param {any} def 如果未找到存储项时的默认返回值，默认为 null
     * @returns {T} 存储项的值，如果未找到或已过期，则返回默认值
     */
    getAccess<T = any>(key: string, def: any = null): T {
      // 获取access密钥
      const accessKey = this.getAccessKey(key);
      // 获取通用渠道密钥
      const prefixKey = this.getKey(key);

      // 尝试从存储中获取访问密钥对应的项
      const item = this.storage.getItem(accessKey) || this.storage.getItem(prefixKey);
      if (item && !this.storage.getItem(accessKey)) {
        // 迁移数据到 accessKey
        this.storage.setItem(accessKey, item);
        this.remove(prefixKey);
      }
      // 如果存储项不存在或解析失败，返回默认值
      return this.parseItem(item, accessKey, def);
    }
    /**
     * 从缓存删除某项
     * @param {string} key
     */
    removeAccess(key: string) {
      this.storage.removeItem(this.getAccessKey(key));
    }
    /**
     * 从缓存删除某项
     * @param {string} key
     */
    remove(key: string) {
      this.storage.removeItem(this.getKey(key));
    }

    /**
     * 清空所有缓存
     * @memberOf Cache
     */
    clear(): void {
      this.storage.clear();
    }
  };
  return new Storage();
};

export const Storage = createStorage();

export default Storage;
